import { Link } from "react-router-dom";
import { Star, ArrowUpRight, Check } from "lucide-react";
import { FadeUp, Chapter, CTAButton } from "@/components/Reveal";
import { PRICES, PRICE_NOTE, REGIO_PLACES } from "@/data/content";

const REVIEWS = [
  { name: "Sanne de Vries", text: "Snelle service en duidelijke communicatie. Alles was netjes geregeld." },
  { name: "Mohamed El Amrani", text: "Energielabel snel aangevraagd en afspraak was zo gemaakt." },
  { name: "Jeroen Bakker", text: "Professioneel, vriendelijk en alles goed uitgelegd." },
  { name: "Lisa van Dijk", text: "Fijne ervaring. Duidelijke prijs en snel geholpen." },
  { name: "Kevin Meijer", text: "Afspraak verliep soepel en het energielabel was snel geregeld." },
  { name: "Nadia A.", text: "Goede communicatie en zeer tevreden over de service." },
  { name: "Thomas Smit", text: "Snel, betrouwbaar en professioneel. Zeker aan te raden." },
  { name: "Melissa K.", text: "Alles verliep makkelijk en zonder gedoe." },
];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
}

export function Testimonials({ number = "06" }) {
  return (
    <section data-testid="reviews-section" className="border-y border-black/5 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Chapter
          number={number}
          label="Klantervaringen"
          title="Wat klanten over ons zeggen"
          description="Woningeigenaren, verhuurders en makelaars over onze werkwijze: van de aanvraag tot de registratie in EP-online."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {REVIEWS.map((r, i) => (
            <FadeUp key={r.name} delay={(i % 4) * 0.08}>
              <figure
                data-testid={`review-card-${i}`}
                className="flex h-full flex-col rounded-3xl border border-black/5 bg-background p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex gap-1 text-primary" aria-label="Vijf sterren">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={15} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-ink/80">
                  &ldquo;{r.text}&rdquo;
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-black/5 pt-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-energy-pale font-display text-xs font-bold text-energy-deep">
                    {initials(r.name)}
                  </span>
                  <span className="font-display text-sm font-semibold text-ink">{r.name}</span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Pricing({ number = "03", showHeader = true }) {
  return (
    <section data-testid="pricing-section" className={showHeader ? "" : "pt-0"}>
      <div className={showHeader ? "mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" : ""}>
        {showHeader && (
          <Chapter
            number={number}
            label="Tarieven"
            title="Duidelijke introductietarieven"
            description="Vaste vanaf-prijzen per woningtype, inclusief woningopname en registratie in EP-online. Zo weet u vooraf waar u aan toe bent."
          />
        )}
        <div className={`grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 ${showHeader ? "mt-14" : ""}`}>
          {PRICES.map((p, i) => (
            <FadeUp key={p.slug} delay={i * 0.07}>
              <div
                data-testid={`price-card-${p.slug}`}
                className="flex h-full flex-col rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <h3 className="font-display text-lg font-medium tracking-tight text-ink">{p.type}</h3>
                <p className="mt-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">vanaf</span>
                  <span className="block font-display text-4xl font-semibold tracking-tight text-ink">€{p.price}</span>
                  <span className="mt-1 block text-xs font-medium text-muted-foreground">incl. btw</span>
                </p>
                <ul className="mt-5 space-y-2 border-t border-black/5 pt-5 text-xs font-medium text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check size={13} strokeWidth={3} className="mt-0.5 shrink-0 text-primary" />
                    EP-W woningopname
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={13} strokeWidth={3} className="mt-0.5 shrink-0 text-primary" />
                    Registratie in EP-online
                  </li>
                </ul>
                <Link
                  to={`/aanvragen?type=${encodeURIComponent(p.type)}`}
                  data-testid={`price-cta-${p.slug}`}
                  className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light"
                >
                  Aanvragen
                  <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.3}>
          <p data-testid="pricing-note" className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {PRICE_NOTE}
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

export function Coverage({ number = "04" }) {
  return (
    <section data-testid="coverage-section" className="border-y border-black/5 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter
              number={number}
              label="Werkgebied"
              title="Actief in Rotterdam en omgeving"
              description="Directelabels voert woningopnames uit in heel de regio Rotterdam en de rest van Zuid-Holland. Woont u net buiten het gebied? Neem contact op, dan kijken we samen naar de mogelijkheden."
            />
            <FadeUp delay={0.2} className="mt-8">
              <CTAButton to="/aanvragen" testid="coverage-cta-aanvragen">
                Start uw aanvraag
              </CTAButton>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {REGIO_PLACES.map((p, i) => (
                <FadeUp key={p} delay={i * 0.04}>
                  <div
                    data-testid={`coverage-place-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-center gap-3 rounded-xl border border-black/10 bg-background px-4 py-3.5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/40"
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-ink">{p}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.4}>
              <p className="mt-5 text-xs font-medium text-muted-foreground">
                En overige plaatsen in Zuid-Holland.
              </p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ZakelijkTeaser() {
  return (
    <section data-testid="zakelijk-teaser" className="bg-energy-deep text-white">
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <FadeUp>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/60">Zakelijk</p>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight md:text-5xl">
                Voor makelaars, verhuurders en vastgoedpartijen
              </h2>
            </FadeUp>
            <FadeUp delay={0.16}>
              <p className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">
                Directelabels is ook beschikbaar voor meerdere woningen, terugkerende opdrachten en structurele
                samenwerkingen — met één vast aanspreekpunt en duidelijke afspraken.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="shrink-0">
            <CTAButton to="/zakelijk" testid="zakelijk-teaser-cta" variant="white">
              Zakelijke samenwerking bespreken
            </CTAButton>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
